/**
 * Client-side Scope of Work helpers (mirrors backend chat-scope-of-work.service).
 */

export function formatUsd(n) {
  const x = Number(n);
  if (!Number.isFinite(x) || x <= 0) return null;
  return `$${Math.round(x).toLocaleString()}`;
}

export function buildSummaryLineFromBands(bands) {
  const pMin = formatUsd(bands.partsMinUsd);
  const pMax = formatUsd(bands.partsMaxUsd);
  const lMin = formatUsd(bands.laborMinUsd);
  const lMax = formatUsd(bands.laborMaxUsd);
  if (!pMin && !lMin) {
    return "Local parts and labor estimates will refine as scope details are confirmed.";
  }
  return `Parts ${pMin || "-"}-${pMax || "-"} · Labor ${lMin || "-"}-${lMax || "-"} (area ranges, not a firm quote)`;
}

export function normalizeStructuredScope(raw) {
  if (!raw || typeof raw !== "object") return null;
  const projectSummary = String(raw.projectSummary || "").trim();
  if (projectSummary.length < 12) return null;

  const est = raw.estimatedCost && typeof raw.estimatedCost === "object"
    ? raw.estimatedCost
    : {};
  const tasks = Array.isArray(raw.detailedTasks) ? raw.detailedTasks : [];

  const detailedTasks = tasks
    .map((t, i) => {
      if (!t || typeof t !== "object") return null;
      const label = String(t.label || "").trim();
      if (!label) return null;
      return {
        id: String(t.id || `task-${i}`),
        label,
        ...(t.quantity ? { quantity: String(t.quantity) } : {}),
        ...(t.notes ? { notes: String(t.notes) } : {}),
      };
    })
    .filter(Boolean);

  return {
    version: "1",
    projectSummary,
    estimatedCost: {
      partsMinUsd: Number(est.partsMinUsd) || undefined,
      partsMaxUsd: Number(est.partsMaxUsd) || undefined,
      laborMinUsd: Number(est.laborMinUsd) || undefined,
      laborMaxUsd: Number(est.laborMaxUsd) || undefined,
      summaryLine:
        String(est.summaryLine || "").trim() ||
        buildSummaryLineFromBands(est),
      ...(est.notes ? { notes: String(est.notes) } : {}),
    },
    detailedTasks,
    ...(raw.materialsPlan ? { materialsPlan: String(raw.materialsPlan) } : {}),
    ...(raw.zipCode
      ? {
          zipCode:
            String(raw.zipCode).match(/\b(\d{5})\b/)?.[1] ||
            String(raw.zipCode).trim(),
        }
      : {}),
    ...(raw.roomType ? { roomType: String(raw.roomType) } : {}),
    ...(raw.projectType ? { projectType: String(raw.projectType) } : {}),
    // structured detail fields (extracted at build time or from backend)
    ...(raw.size ? { size: String(raw.size) } : {}),
    ...(raw.propertyType ? { propertyType: String(raw.propertyType) } : {}),
    ...(raw.timeline ? { timeline: String(raw.timeline) } : {}),
    ...(raw.occupancy ? { occupancy: String(raw.occupancy) } : {}),
    ...(raw.finishes ? { finishes: String(raw.finishes) } : {}),
  };
}

const QA_PERSONA_RE = /\b(?:MPGM|SRPE|LPGM|MPSM|LPPE|MPPE|SPEE|SREE|MREE|MPEE|LREE|LPEE)-\d+\b/gi;

function stripChatBoilerplate(text) {
  return String(text || "")
    .replace(/\*\*Clumse AI\*\*:.*?(\n|$)/gi, "")
    .replace(/Clumse AI:.*?(\n|$)/gi, "")
    .replace(
      /\b(?:MPGM|SRPE|LPGM|MPSM|LPPE|MPPE|SPEE|SREE|MREE|MPEE|LREE|LPEE)-\d+\b[^\n.!?]{0,120}?(?:general\s+)?contractor\.?\s*/gi,
      ""
    )
    .replace(
      /\b(?:MPGM|SRPE|LPGM|MPSM|LPPE|MPPE|SPEE|SREE|MREE|MPEE|LREE|LPEE)-\d+\b/gi,
      ""
    )
    .replace(/\bwith general contractor\b/gi, "")
    .replace(/Tell me what's going on.*?photos welcome\)?\.?\s*/gi, "")
    .replace(/What brought you here today\?\s*/gi, "")
    .replace(/Hi!?\s*I'm Clumse(?: AI)?[^.]*\.\s*/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/** Slot-fill answers that should never win as the project summary. */
const SLOT_FILL_RE =
  /^\d{5}(?:-\d{4})?$|^(?:single[- ]?family|townhome|condo|multi[- ]family|single family home)\b|^(?:home is|the home is|vacant|occupied)\b|^(?:urgent|not urgent|needs completed|within \d+|still planning)\b|^(?:yes|no|nope|nothing)\b|^(?:that is it|that's it|nothing else|no more|all done|all good)\b|^(?:next \d+[-–\d]*\s*(?:days?|weeks?|months?)?)\b|^(?:high[- ]?end|standard|budget|luxury|mid[- ]?range)\s+(?:finishes?|fixtures?|materials?|quality)?\b|^(?:high[- ]?end|standard|budget|luxury|mid[- ]?range)\s*$/i;

function extractConciseUserProblem(conversationText) {
  const cleaned = stripChatBoilerplate(conversationText);
  if (cleaned.length < 16) return "";
  if (cleaned.length <= 220) return cleaned.slice(0, 200);

  const sentences = cleaned
    .split(/(?<=[.!?])\s+|\n+/)
    .map((s) => s.trim())
    .filter((s) => s.length >= 12);

  const userLike = sentences.filter(
    (s) =>
      !QA_PERSONA_RE.test(s) &&
      !/^(hi!?|i'm|tell me|what brought|clumse)/i.test(s) &&
      !SLOT_FILL_RE.test(s)
  );

  if (userLike.length === 0) return cleaned.slice(0, 200);

  // Prefer the FIRST substantive sentence (original project description), then
  // up to 2 additional detail sentences for supplemental scope info.
  const first = userLike[0];
  const extras = userLike.slice(1, 3).filter((s) => !SLOT_FILL_RE.test(s));
  return [first, ...extras].join(" ").trim().slice(0, 400);
}

// ─── structured-detail extractors ──────────────────────────────────────────

function extractPropertyType(text) {
  if (/\btown[- ]?home\b/i.test(text)) return "Townhome";
  if (/\bcondo(?:minium)?\b/i.test(text)) return "Condo";
  if (/\bsingle[- ]?family\b/i.test(text)) return "Single-family home";
  if (/\bmulti[- ]?family\b/i.test(text)) return "Multi-family";
  return undefined;
}

function extractOccupancy(text) {
  if (/\b(we will be|will be|living there|living here|occupied during|staying there)\b/i.test(text)) return "Owner-occupied during work";
  if (/\b(home is vacant|property is vacant|vacant|empty|not living)\b/i.test(text)) return "Vacant";
  if (/\b(rental|tenant|rented)\b/i.test(text)) return "Rental";
  return undefined;
}

function parseTimelineUserReply(raw) {
  const u = String(raw || "").trim().toLowerCase();
  if (!u) return undefined;
  if (/^\s*whenever\s*[.!]?\s*$/.test(u)) return "researching";
  if (
    /\b(whenever|flexible|any\s*time|anytime|no\s+rush|not\s+urgent|can\s+wait|no\s+hurry)\b/i.test(
      u
    )
  ) {
    return "researching";
  }
  if (/\b(still\s+planning|research|exploring|considering|someday|planning)\b/i.test(u)) {
    return "researching";
  }
  if (
    /\b(within\s+\d+|next\s+\d+|\d+\s*days?|30\s+days|soon)\b/i.test(u) &&
    !/\bnot\s+urgent\b/i.test(u)
  ) {
    return "planned";
  }
  if (
    /\b(urgent|asap|emergency|today|tomorrow|this\s+week)\b/i.test(u) &&
    !/\bnot\s+urgent\b/i.test(u)
  ) {
    return "emergency";
  }
  return undefined;
}

/** Authoritative timeline from direct Q&A (locks once user answers the timeline prompt). */
export function resolveLockedTimelineFromMessages(messages) {
  if (!Array.isArray(messages)) return undefined;
  let locked;
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (m?.role !== "assistant" && m?.from !== "assistant") continue;
    const aq = String(m.content || m.text || "");
    if (
      !/timeline|when\s+do\s+you|how\s+soon|urgent|within\s+30|still\s+planning|no\s+rush/i.test(
        aq
      )
    ) {
      continue;
    }
    for (let j = i + 1; j < messages.length; j++) {
      const um = messages[j];
      if (um?.role !== "user" && um?.from !== "user") continue;
      const parsed = parseTimelineUserReply(String(um.content || um.text || ""));
      if (parsed) locked = parsed;
      break;
    }
  }
  return locked;
}

function extractTimeline(text) {
  // Flexible / negations before positive urgency keywords.
  if (/\b(whenever|flexible\s+on\s+timing|flexible)\b/i.test(text)) {
    return "researching";
  }
  if (/\b(not\s+urgent|no\s+rush|no\s+hurry|can\s+wait|not\s+an?\s+emergency)\b/i.test(text)) {
    // Look for an explicit day count even when "not urgent" is present
    const notUrgentDays = text.match(/\b(?:next|within)\s+(\d+)\s*(?:days?|fays?|[a-z]{1,5})\b/i);
    if (notUrgentDays) return `Within ${notUrgentDays[1]} days`;
    return "Not urgent";
  }
  if (/\b(urgent|asap|right away|immediately|emergency)\b/i.test(text)) return "emergency";

  // Range: "next 30-40 days" / "30-40 fays" (typo tolerance via [a-z]{1,5})
  const daysRangeMatch = text.match(/\b(?:next\s+)?(\d+)[-–](\d+)\s*(?:days?|fays?|[a-z]{1,5})\b/i);
  if (daysRangeMatch) return `${daysRangeMatch[1]}–${daysRangeMatch[2]} days`;

  // Single: "within 30 days", "next 30 days", "next 30 fays" (typo)
  const withinMatch = text.match(/\bwithin\s+(\d+)\s*(?:days?|fays?)\b/i);
  if (withinMatch) return `Within ${withinMatch[1]} days`;

  // "next N days/fays/[word]" - catch typos via the trailing word group
  const nextMatch = text.match(/\bnext\s+(\d+)\s*(?:days?|fays?|[a-z]{1,8})\b/i);
  if (nextMatch) return `Within ${nextMatch[1]} days`;

  // "needs completed within N days"
  const neededMatch = text.match(/\bneeds?\s+completed?\s+within\s+(\d+)\s*(?:days?|fays?)\b/i);
  if (neededMatch) return `Within ${neededMatch[1]} days`;

  // Plain "N days" at end of phrase
  const plainDaysMatch = text.match(/\b(\d+)\s*(?:days?|fays?)\b/i);
  if (plainDaysMatch) return `Within ${plainDaysMatch[1]} days`;

  if (/\b(still planning|planning stage|just browsing|not sure yet)\b/i.test(text)) {
    return "researching";
  }
  return undefined;
}

function extractFinishes(text) {
  if (/\bhigh[- ]?end\b/i.test(text) && /\b(fixtures?|finishes?|materials?|appliances?)\b/i.test(text)) return "High-end";
  if (/\bluxury\b/i.test(text)) return "Luxury";
  if (/\bhigh[- ]?end\b/i.test(text)) return "High-end";
  if (/\bmid[- ]?range\b/i.test(text)) return "Mid-range";
  if (/\bstandard\b/i.test(text) && /\b(fixtures?|finishes?|materials?)\b/i.test(text)) return "Standard";
  if (/\bbudget\b/i.test(text)) return "Budget";
  return undefined;
}

/**
 * Infer project size label (Small / Medium / Large) from conversation text.
 * Mirrors the small | medium | large taxonomy used by surgical intake on the backend.
 */
function extractProjectSize(text) {
  // Explicit size/magnitude language
  if (/\b(small|minor|simple|handyman|quick)\b.{0,30}\b(project|job|repair|remodel|renovation|fix)\b/i.test(text)) return "Small";
  if (/\b(project|job|repair|remodel|renovation)\b.{0,30}\b(small|minor|simple)\b/i.test(text)) return "Small";
  if (/\b(large|major|extensive|big|significant|full)\b.{0,30}\b(project|job|repair|remodel|renovation|overhaul)\b/i.test(text)) return "Large";
  if (/\b(project|job|remodel|renovation)\b.{0,30}\b(large|major|extensive|full)\b/i.test(text)) return "Large";
  if (/\b(medium|moderate|mid[- ]?size[d]?)\b/i.test(text)) return "Medium";
  // Outdoor area cues → Large
  if (/\b(1\/4\s*acre|quarter[\s-]acre|half[\s-]acre|full\s+yard|entire\s+yard|whole\s+yard)\b/i.test(text)) return "Large";
  return undefined;
}

const FINAL_DETAILS_ASKED_RE =
  /anything else about the project|factor in before we line up vendor|what else should I know for accurate local quotes|anything else.*factored in/i;

const USER_DONE_WITH_INTAKE_RE =
  /(?:^|\b)(nope|no|nothing|that'?s\s+it|that\s+is\s+it|all\s+set|we'?re\s+good|nothing\s+else|all\s+good|no\s+more|that'?s\s+all)(?:\b|[,.!]|$)/i;

function userExpressesIntakeFinality(text) {
  const t = String(text || "").trim();
  if (!t) return false;
  if (USER_DONE_WITH_INTAKE_RE.test(t)) return true;
  if (
    /\b(that'?s\s+it|that\s+is\s+it|nothing\s+else|all\s+good|all\s+set|we'?re\s+good|no\s+more|that'?s\s+all)\b/i.test(
      t
    )
  ) {
    return true;
  }
  return false;
}

function isScopeMetaCommentary(text) {
  const t = String(text || "").trim();
  if (!t) return false;
  return /\b(it could be|might be|could be|sounds like)\s+(?:a\s+)?(?:long|hard|big|tough|difficult|involved)\s+(?:job|project)\b/i.test(
    t
  );
}

/** Conversational / pricing feedback — not structural scope (mirrors backend). */
function isConversationalNonScopeText(text) {
  const t = String(text || "").trim();
  if (!t || t.length < 3) return true;
  if (userExpressesIntakeFinality(t)) return true;
  if (isScopeMetaCommentary(t)) return true;
  if (/^(thanks?|thank\s+you|thx|ok(?:ay)?|got\s+it)\b/i.test(t)) return true;
  if (
    /\b(price|pricing|ridiculous|outrageous|too\s+(?:much|high|expensive)|search\s+fee|one[- ]time\s+fee|pay\s+\$|why\s+\$|\$\d+)/i.test(
      t
    )
  ) {
    return true;
  }
  if (
    /\b(not\s+fair|don'?t\s+want\s+to\s+pay|won'?t\s+pay|refund|charge\s+me|loop|frustrat)/i.test(
      t
    )
  ) {
    return true;
  }
  if (/^\s*(no|nope|nah)\s*[.!]?\s*$/i.test(t)) return true;
  return false;
}

/** User scope additions after the pre-checkout "anything else?" prompt (mirrors backend). */
export function collectSupplementalScopeDetails(messages) {
  if (!Array.isArray(messages) || !messages.length) return [];

  let lastAskIdx = -1;
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (
      (m?.role === "assistant" || m?.from === "assistant") &&
      FINAL_DETAILS_ASKED_RE.test(String(m.content || m.text || ""))
    ) {
      lastAskIdx = i;
    }
  }
  if (lastAskIdx < 0) return [];

  const extras = [];
  for (let i = lastAskIdx + 1; i < messages.length; i++) {
    const m = messages[i];
    if (m?.role !== "user" && m?.from !== "user") continue;
    const text = String(m.content || m.text || "").trim();
    if (text.length < 4) continue;
    if (USER_DONE_WITH_INTAKE_RE.test(text)) continue;
    if (
      /^\s*(yes|yeah|yep|sure|ready|i'?m\s+ready|that'?s\s+it|nothing|nope|no)\s*[.!]?\s*$/i.test(
        text
      )
    ) {
      continue;
    }
    if (/^\d{5}(?:-\d{4})?$/.test(text)) continue;
    if (isConversationalNonScopeText(text)) continue;
    extras.push(text);
  }
  return extras;
}

function dedupeSentences(text) {
  const parts = String(text || "")
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const p of parts) {
    const key = p.toLowerCase().slice(0, 48);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out.join(" ").replace(/\s{2,}/g, " ").trim();
}

/** Merge live user additions from the "anything else?" phase into the scope summary. */
export function mergeSupplementalIntoScope(scope, messages) {
  if (!scope || !Array.isArray(messages) || !messages.length) return scope;
  const extras = collectSupplementalScopeDetails(messages);
  if (!extras.length) return scope;

  const base = String(scope.projectSummary || "").trim();
  const missing = extras.filter(
    (e) =>
      !isConversationalNonScopeText(e) &&
      !base.toLowerCase().includes(e.toLowerCase().slice(0, Math.min(24, e.length)))
  );
  if (!missing.length) return scope;

  return {
    ...scope,
    projectSummary: dedupeSentences(`${base}. ${missing.join(". ")}`).slice(0, 520),
  };
}

/** Build a minimal scope from chat text when the server has not emitted one yet. */
export function buildClientScopeFallback(conversationText, messages = []) {
  const text = String(conversationText || "").trim();
  if (text.length < 28) return null;

  const zip = text.match(/\b(\d{5})(?:-\d{4})?\b/)?.[1];
  const bathroomExcluded =
    /\b(no\s+(?:work\s+for\s+)?bathroom|no\s+bathroom\s+work|just\s+the\s+bedroom)\b/i.test(
      text
    );
  const isBedroom =
    /\b(bedroom|master\s+bedroom)\b/i.test(text) &&
    /\b(flooring|hardwood|carpet|closet|blinds?|remodel)\b/i.test(text);
  const isBath =
    !bathroomExcluded &&
    !isBedroom &&
    /\b(bathroom|powder\s+room|toilet|faucet|sink)\b/i.test(text) &&
    !/\b(full\s+bath|adjacent)\b/i.test(text);
  const isKitchen = /\bkitchen\b/i.test(text);
  const isRemodel = /\bremodel|renovation|renovate|gut|makeover\b/i.test(text);
  const roomType = isBedroom
    ? "bedroom"
    : isBath
      ? "bathroom"
      : isKitchen
        ? "kitchen"
        : undefined;
  const projectType = isRemodel ? "remodel" : "repair";

  const propertyType = extractPropertyType(text);
  const occupancy = extractOccupancy(text);
  const lockedTimeline = messages?.length
    ? resolveLockedTimelineFromMessages(messages)
    : undefined;
  const timeline = lockedTimeline || extractTimeline(text);
  const finishes = extractFinishes(text);
  const size = extractProjectSize(text);

  const userProblem = extractConciseUserProblem(text);

  // Build a clean prose description (just the project description, no raw metadata)
  const projectSummary = userProblem || (
    [
      roomType ? `${roomType} ${projectType}` : projectType,
      propertyType,
      zip || null,
    ]
      .filter(Boolean)
      .join(" · ")
  );

  const scope = normalizeStructuredScope({
    version: "1",
    projectSummary,
    estimatedCost: {
      summaryLine:
        "Parts and labor breakdown will update as Clumse confirms size, finishes, and materials plan.",
    },
    detailedTasks: [],
    ...(zip ? { zipCode: zip } : {}),
    ...(roomType ? { roomType } : {}),
    projectType,
    ...(size ? { size } : {}),
    ...(propertyType ? { propertyType } : {}),
    ...(occupancy ? { occupancy } : {}),
    ...(timeline ? { timeline } : {}),
    ...(finishes ? { finishes } : {}),
  });

  return mergeSupplementalIntoScope(scope, messages);
}

export function formatScopeAsCleanText(scope) {
  if (!scope) return "";
  const lines = [];
  lines.push("SCOPE OF WORK");
  lines.push("");
  lines.push("PROJECT SUMMARY");
  lines.push(scope.projectSummary);
  if (scope.materialsPlan) {
    lines.push("");
    lines.push(`Materials: ${scope.materialsPlan}`);
  }
  // Structured details
  const details = [
    scope.zipCode ? scope.zipCode : null,
    scope.size ? `Project size: ${scope.size.charAt(0).toUpperCase() + scope.size.slice(1)}` : null,
    scope.propertyType ? `Property: ${scope.propertyType}` : null,
    scope.occupancy ? `Occupancy: ${scope.occupancy}` : null,
    scope.timeline ? `Timeline: ${scope.timeline}` : null,
    scope.finishes ? `Finishes: ${scope.finishes}` : null,
  ].filter(Boolean);
  if (details.length > 0) {
    lines.push("");
    lines.push("PROJECT DETAILS");
    details.forEach((d) => lines.push(d));
  }
  lines.push("");
  lines.push("ESTIMATED LOCAL PARTS & LABOR");
  lines.push(scope.estimatedCost?.summaryLine || "");
  if (scope.estimatedCost?.notes) lines.push(scope.estimatedCost.notes);
  const tasks = scope.detailedTasks || [];
  if (tasks.length > 0) {
    lines.push("");
    lines.push("DETAILED TASKS");
    tasks.forEach((task, i) => {
      const qty = task.quantity ? ` (${task.quantity})` : "";
      const note = task.notes ? ` - ${task.notes}` : "";
      lines.push(`${i + 1}. ${task.label}${qty}${note}`);
    });
  }
  return lines.join("\n").trim();
}

export function findLatestStructuredScopeInMessages(messages) {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m?.role !== "assistant") continue;
    const raw =
      m.metadata?.structuredScope ?? m.metadata?.scopeOfWork ?? null;
    const norm = normalizeStructuredScope(raw);
    if (norm) return norm;
  }
  return null;
}
